import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { X, Users, Calendar, Settings, Camera } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/ui/use-toast';
import { Event, EventType, EventVisibility, Employee } from '@/types/index';
import { UnifiedEmployeeSelect } from '@/components/ui/unified-employee-select';

interface EventFormDialogProps {
  businessId: string;
  event?: Event | null;
  onClose: () => void;
}

const EventFormDialog: React.FC<EventFormDialogProps> = ({ businessId, event, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    event_date: '',
    start_time: '',
    duration_minutes: 60,
    location: '',
    capacity: '',
    is_voluntary: true,
    // New enhanced fields
    event_type: 'employee_voluntary' as EventType,
    visibility: 'internal' as EventVisibility,
    max_external_attendees: '',
    registration_deadline: '',
    registration_required: false,
    external_registration_enabled: false,
    event_image_url: '',
    event_tags: [] as string[],
    target_audience: [] as string[],
  });
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (event) {
      setFormData({
        name: event.name,
        description: event.description || '',
        event_date: event.event_date,
        start_time: event.start_time,
        duration_minutes: event.duration_minutes,
        location: event.location || '',
        capacity: event.capacity?.toString() || '',
        is_voluntary: event.is_voluntary,
        // New enhanced fields
        event_type: event.event_type || 'employee_voluntary',
        visibility: event.visibility || 'internal',
        max_external_attendees: event.max_external_attendees?.toString() || '',
        registration_deadline: event.registration_deadline || '',
        registration_required: event.registration_required || false,
        external_registration_enabled: event.external_registration_enabled || false,
        event_image_url: event.event_image_url || '',
        event_tags: event.event_tags || [],
        target_audience: event.target_audience || [],
      });
      loadEventAssignments();
    }
  }, [event]);


  const loadEventAssignments = async () => {
    if (!event) return;

    try {
      const { data, error } = await supabase
        .from('event_assignments')
        .select('employee_id')
        .eq('event_id', event.id);

      if (error) throw error;
      setSelectedEmployees(data?.map(a => a.employee_id) || []);
    } catch (error) {
      console.error('Error loading event assignments:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    console.log('Starting event submission...');
    console.log('Form data:', formData);

    // Validate date is not in the past
    const eventDate = new Date(formData.event_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (eventDate < today) {
      toast.error("Cannot create events in the past", {
        description: "Please select a future date for the event.",
      });
      setLoading(false);
      return;
    }

    try {
      const user = await supabase.auth.getUser();
      console.log('Current user:', user.data.user?.id);

      const eventData = {
        ...formData,
        business_id: businessId,
        capacity: formData.capacity ? parseInt(formData.capacity) : null,
        max_external_attendees: formData.max_external_attendees ? parseInt(formData.max_external_attendees) : null,
        registration_deadline: formData.registration_deadline || null,
        created_by: user.data.user?.id,
        // Set is_voluntary based on event_type for backward compatibility
        is_voluntary: formData.event_type === 'employee_voluntary',
      };

      console.log('Event data to be inserted:', eventData);

      let eventId: string;

      if (event) {
        console.log('Updating existing event:', event.id);
        // Update existing event
        const { error } = await supabase
          .from('events')
          .update(eventData)
          .eq('id', event.id);

        if (error) {
          console.error('Error updating event:', error);
          throw error;
        }
        eventId = event.id;

        // Delete existing assignments
        console.log('Deleting existing assignments...');
        const { error: deleteError } = await supabase
          .from('event_assignments')
          .delete()
          .eq('event_id', event.id);

        if (deleteError) {
          console.error('Error deleting existing assignments:', deleteError);
          throw deleteError;
        }
      } else {
        console.log('Creating new event...');
        // Create new event
        const { data, error } = await supabase
          .from('events')
          .insert([eventData])
          .select()
          .single();

        if (error) {
          console.error('Error creating event:', error);
          throw error;
        }
        console.log('Event created successfully:', data);
        eventId = data.id;
      }

      // Create event assignments
      if (selectedEmployees.length > 0) {
        console.log('Creating event assignments for employees:', selectedEmployees);
        const assignments = selectedEmployees.map(employeeId => ({
          event_id: eventId,
          employee_id: employeeId,
          attendance_status: 'pending',
        }));

        console.log('Assignment data:', assignments);

        const { error: assignmentError } = await supabase
          .from('event_assignments')
          .insert(assignments);

        if (assignmentError) {
          console.error('Error creating assignments:', assignmentError);
          throw assignmentError;
        }
        console.log('Event assignments created successfully');
      }

      toast.success(`Event ${event ? 'updated' : 'created'} successfully!`);
      
      console.log('Event operation completed successfully');
      
      // Close the dialog after successful operation
      onClose();
    } catch (error) {
      console.error('Error saving event:', error);
      toast.error(`Failed to ${event ? 'update' : 'create'} event. ${error instanceof Error ? error.message : String(error) || 'Please try again.'}`);
    } finally {
      setLoading(false);
    }
  };


  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {event ? 'Edit Event' : 'Create New Event'}
          </DialogTitle>
          <DialogDescription>
            {event ? 'Update the event details and employee assignments.' : 'Create a new event and assign employees to it.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Event Type Selection */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="event_type">Event Type *</Label>
              <Select
                value={formData.event_type}
                onValueChange={(value: EventType) => {
                  setFormData({ 
                    ...formData, 
                    event_type: value,
                    is_voluntary: value === 'employee_voluntary',
                    visibility: value === 'customer' ? 'public' : 'internal',
                    external_registration_enabled: value === 'customer',
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select event type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="customer">Customer Event (Happy Hours, Promotions)</SelectItem>
                  <SelectItem value="employee_voluntary">Employee Voluntary (Team Building)</SelectItem>
                  <SelectItem value="employee_mandatory">Employee Mandatory (Meetings)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Event Type Indicator */}
            <div className="flex items-center gap-2">
              <Badge 
                variant={formData.event_type === 'customer' ? 'default' : 
                        formData.event_type === 'employee_voluntary' ? 'secondary' : 'outline'}
              >
                {formData.event_type === 'customer' && '🎉 Customer Event'}
                {formData.event_type === 'employee_voluntary' && '🤝 Voluntary Employee Event'}
                {formData.event_type === 'employee_mandatory' && '📋 Mandatory Employee Event'}
              </Badge>
            </div>
          </div>

          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="basic">
                <Calendar className="h-4 w-4 mr-2" />
                Basic Info
              </TabsTrigger>
              <TabsTrigger value="audience">
                <Users className="h-4 w-4 mr-2" />
                {formData.event_type === 'customer' ? 'Customer Settings' : 'Employee Assignment'}
              </TabsTrigger>
              <TabsTrigger value="advanced">
                <Settings className="h-4 w-4 mr-2" />
                Advanced
              </TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="name">Event Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder={
                      formData.event_type === 'customer' ? 'e.g., Happy Hour Friday' :
                      formData.event_type === 'employee_voluntary' ? 'e.g., Team Building Activity' :
                      'e.g., Monthly All-Hands Meeting'
                    }
                    required
                  />
                </div>
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={
                    formData.event_type === 'customer' ? 'Describe the promotion, activities, and what customers can expect...' :
                    formData.event_type === 'employee_voluntary' ? 'What will happen at this team building event?' :
                    'Meeting agenda and key topics to be discussed...'
                  }
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="event_date">Date *</Label>
                  <Input
                    id="event_date"
                    type="date"
                    value={formData.event_date}
                    onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="start_time">Start Time *</Label>
                  <Input
                    id="start_time"
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="duration_minutes">Duration (minutes) *</Label>
                  <Input
                    id="duration_minutes"
                    type="number"
                    min="15"
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="Where will this event take place?"
                  />
                </div>

                <div>
                  <Label htmlFor="capacity">
                    {formData.event_type === 'customer' ? 'Expected Attendees' : 'Capacity'} (optional)
                  </Label>
                  <Input
                    id="capacity"
                    type="number"
                    min="1"
                    value={formData.capacity}
                    onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                    placeholder={formData.event_type === 'customer' ? 'How many customers do you expect?' : 'Maximum number of attendees'}
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="audience" className="space-y-4">
              {formData.event_type === 'customer' ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="max_external_attendees">Registration Limit</Label>
                      <Input
                        id="max_external_attendees"
                        type="number"
                        min="1"
                        value={formData.max_external_attendees}
                        onChange={(e) => setFormData({ ...formData, max_external_attendees: e.target.value })}
                        placeholder="Maximum customer registrations"
                      />
                    </div>

                    <div>
                      <Label htmlFor="registration_deadline">Registration Deadline</Label>
                      <Input
                        id="registration_deadline"
                        type="date"
                        value={formData.registration_deadline}
                        onChange={(e) => setFormData({ ...formData, registration_deadline: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="registration_required"
                      checked={formData.registration_required}
                      onCheckedChange={(checked) => setFormData({ ...formData, registration_required: checked })}
                    />
                    <Label htmlFor="registration_required">
                      Require advance registration (customers must sign up beforehand)
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="external_registration_enabled"
                      checked={formData.external_registration_enabled}
                      onCheckedChange={(checked) => setFormData({ ...formData, external_registration_enabled: checked })}
                    />
                    <Label htmlFor="external_registration_enabled">
                      Enable online customer registration
                    </Label>
                  </div>
                </div>
              ) : (
                <div>
                  <Label>Assign Employees</Label>
                  <Card className="mt-2">
                    <CardContent className="p-4">
                      <UnifiedEmployeeSelect
                        businessId={businessId}
                        value={selectedEmployees}
                        onValueChange={(value) => {
                          setSelectedEmployees(Array.isArray(value) ? value : []);
                        }}
                        variant="checkbox-list"
                        config={{
                          mode: 'multiple',
                          includeInactive: false,
                          sortBy: 'name',
                        }}
                        placeholder="Select employees for this event"
                        emptyMessage="No employees found"
                        showRoleInfo={true}
                        onSelectionChange={(employees) => {
                          // Optional: Handle selection change for additional logic
                        }}
                        className="max-h-48 overflow-y-auto"
                      />
                    </CardContent>
                  </Card>
                  {selectedEmployees.length > 0 && (
                    <div className="mt-2">
                      <Badge variant="outline">
                        {selectedEmployees.length} employee{selectedEmployees.length !== 1 ? 's' : ''} selected
                      </Badge>
                    </div>
                  )}

                  {formData.event_type === 'employee_mandatory' && (
                    <div className="mt-4 p-3 bg-orange-50 rounded-md">
                      <p className="text-sm text-orange-800">
                        <strong>Mandatory Event:</strong> All selected employees will be required to attend.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>

            <TabsContent value="advanced" className="space-y-4">
              <div>
                <Label htmlFor="visibility">Visibility</Label>
                <Select
                  value={formData.visibility}
                  onValueChange={(value: EventVisibility) => setFormData({ ...formData, visibility: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="public">Public (visible to everyone)</SelectItem>
                    <SelectItem value="internal">Internal (employees only)</SelectItem>
                    <SelectItem value="private">Private (selected people only)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="event_image_url">Event Image URL (optional)</Label>
                <Input
                  id="event_image_url"
                  type="url"
                  value={formData.event_image_url}
                  onChange={(e) => setFormData({ ...formData, event_image_url: e.target.value })}
                  placeholder="https://example.com/event-image.jpg"
                />
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : (event ? 'Update Event' : 'Create Event')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EventFormDialog;